import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Download,
  MessageSquare,
  Star,
  BarChart3,
  Filter,
  Search,
  Calendar,
  FileText,
  Users,
  TrendingUp,
  Award,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Paperclip,
  Send,
  Save,
  RotateCcw,
  ArrowLeft,
  Plus,
  Edit3,
  Trash2,
  MoreVertical,
} from "lucide-react";
import {
  Assignment,
  AssignmentSubmission,
  AssignmentGrade,
  AssignmentAnalytics,
  GradingRubric,
  GradingRubricCriterion,
  FeedbackComment,
} from "../../types/elearning";
import apiClient from "../../services/api";

interface GradingState {
  assignment: Assignment | null;
  submissions: AssignmentSubmission[];
  selectedSubmission: AssignmentSubmission | null;
  rubric: GradingRubric | null;
  analytics: AssignmentAnalytics | null;
  loading: boolean;
  error: string | null;
  filterStatus: "all" | "submitted" | "graded" | "late" | "pending";
  searchQuery: string;
  gradingMode: "individual" | "bulk";
  selectedSubmissions: string[];
  currentGrade: Partial<AssignmentGrade>;
  feedback: string;
  showRubric: boolean;
  showAnalytics: boolean;
}

interface SubmissionListProps {
  submissions: AssignmentSubmission[];
  selectedSubmission: AssignmentSubmission | null;
  onSelectSubmission: (submission: AssignmentSubmission) => void;
  searchQuery: string;
  filterStatus: string;
}

const SubmissionList: React.FC<SubmissionListProps> = ({
  submissions,
  selectedSubmission,
  onSelectSubmission,
  searchQuery,
  filterStatus,
}) => {
  const filteredSubmissions = submissions.filter((submission) => {
    const matchesSearch =
      !searchQuery ||
      submission.studentName
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      submission.studentId.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFilter =
      filterStatus === "all" || submission.status === filterStatus;

    return matchesSearch && matchesFilter;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "graded":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "submitted":
        return <Clock className="w-4 h-4 text-blue-600" />;
      case "late":
        return <AlertTriangle className="w-4 h-4 text-orange-600" />;
      default:
        return <XCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "graded":
        return "bg-green-100 text-green-800";
      case "submitted":
        return "bg-blue-100 text-blue-800";
      case "late":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      <div className="p-4 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900 flex items-center">
          <Users className="w-4 h-4 mr-2" />
          Submissions ({filteredSubmissions.length})
        </h3>
      </div>

      <div className="max-h-96 overflow-y-auto">
        {filteredSubmissions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No submissions found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredSubmissions.map((submission) => (
              <button
                key={submission._id}
                onClick={() => onSelectSubmission(submission)}
                className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                  selectedSubmission?._id === submission._id
                    ? "bg-blue-50 border-r-2 border-blue-600"
                    : ""
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-medium text-gray-900">
                        {submission.studentName}
                      </span>
                      {getStatusIcon(submission.status)}
                    </div>

                    <div className="text-sm text-gray-600 space-y-1">
                      <p>ID: {submission.studentId}</p>
                      <p>Submitted: {formatDate(submission.submittedAt)}</p>

                      {submission.grade && (
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">Grade:</span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs ${
                              submission.grade.score >= 90
                                ? "bg-green-100 text-green-800"
                                : submission.grade.score >= 80
                                ? "bg-blue-100 text-blue-800"
                                : submission.grade.score >= 70
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {submission.grade.score}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="ml-4">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${getStatusColor(
                        submission.status
                      )}`}
                    >
                      {submission.status}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface RubricGradingProps {
  rubric: GradingRubric;
  currentGrade: Partial<AssignmentGrade>;
  onGradeChange: (criterion: string, score: number, feedback?: string) => void;
}

const RubricGrading: React.FC<RubricGradingProps> = ({
  rubric,
  currentGrade,
  onGradeChange,
}) => {
  const [expandedCriteria, setExpandedCriteria] = useState<string[]>([]);

  const toggleCriterion = (criterionId: string) => {
    setExpandedCriteria((prev) =>
      prev.includes(criterionId)
        ? prev.filter((id) => id !== criterionId)
        : [...prev, criterionId]
    );
  };

  const getCriterionGrade = (criterionId: string) => {
    return currentGrade.rubricScores?.[criterionId] || 0;
  };

  const getCriterionFeedback = (criterionId: string) => {
    return currentGrade.criteriaFeedback?.[criterionId] || "";
  };

  const getScoreColor = (score: number, maxScore: number) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 90) return "text-green-600";
    if (percentage >= 80) return "text-blue-600";
    if (percentage >= 70) return "text-yellow-600";
    return "text-red-600";
  };

  const totalScore = rubric.criteria.reduce((sum, criterion) => {
    return sum + (currentGrade.rubricScores?.[criterion._id] || 0);
  }, 0);

  const maxScore = rubric.criteria.reduce(
    (sum, criterion) => sum + criterion.maxScore,
    0
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Grading Rubric</h3>
          <div className="text-right">
            <div className="text-sm text-gray-600">Total Score</div>
            <div
              className={`text-lg font-bold ${getScoreColor(
                totalScore,
                maxScore
              )}`}
            >
              {totalScore} / {maxScore}
            </div>
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {rubric.criteria.map((criterion) => (
          <div key={criterion._id} className="p-4">
            <div className="flex items-center justify-between mb-2">
              <button
                onClick={() => toggleCriterion(criterion._id)}
                className="flex items-center space-x-2 text-left"
              >
                {expandedCriteria.includes(criterion._id) ? (
                  <ChevronUp className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
                <span className="font-medium text-gray-900">
                  {criterion.name}
                </span>
              </button>

              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="0"
                  max={criterion.maxScore}
                  value={getCriterionGrade(criterion._id)}
                  onChange={(e) =>
                    onGradeChange(
                      criterion._id,
                      Math.min(
                        parseInt(e.target.value) || 0,
                        criterion.maxScore
                      )
                    )
                  }
                  className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="text-sm text-gray-600">
                  / {criterion.maxScore}
                </span>
              </div>
            </div>

            {expandedCriteria.includes(criterion._id) && (
              <div className="mt-3 space-y-3">
                <p className="text-sm text-gray-600">{criterion.description}</p>

                {criterion.levels && criterion.levels.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                    {criterion.levels.map((level, index) => (
                      <button
                        key={index}
                        onClick={() =>
                          onGradeChange(criterion._id, level.score)
                        }
                        className={`p-2 text-xs border rounded-lg text-left transition-colors ${
                          getCriterionGrade(criterion._id) === level.score
                            ? "border-blue-500 bg-blue-50 text-blue-800"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="font-medium">{level.name}</div>
                        <div className="text-gray-600">{level.score} pts</div>
                        <div className="mt-1">{level.description}</div>
                      </button>
                    ))}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Feedback for this criterion
                  </label>
                  <textarea
                    value={getCriterionFeedback(criterion._id)}
                    onChange={(e) =>
                      onGradeChange(
                        criterion._id,
                        getCriterionGrade(criterion._id),
                        e.target.value
                      )
                    }
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Provide specific feedback for this criterion..."
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

interface AnalyticsOverviewProps {
  analytics: AssignmentAnalytics;
}

const AnalyticsOverview: React.FC<AnalyticsOverviewProps> = ({ analytics }) => {
  const getGradeDistribution = () => {
    const ranges = [
      { name: "A (90-100%)", min: 90, max: 100, color: "bg-green-500" },
      { name: "B (80-89%)", min: 80, max: 89, color: "bg-blue-500" },
      { name: "C (70-79%)", min: 70, max: 79, color: "bg-yellow-500" },
      { name: "D (60-69%)", min: 60, max: 69, color: "bg-orange-500" },
      { name: "F (0-59%)", min: 0, max: 59, color: "bg-red-500" },
    ];

    return ranges.map((range) => {
      const count = analytics.gradeDistribution.filter(
        (grade) => grade >= range.min && grade <= range.max
      ).length;

      const percentage =
        analytics.totalSubmissions > 0
          ? (count / analytics.totalSubmissions) * 100
          : 0;

      return { ...range, count, percentage };
    });
  };

  const distribution = getGradeDistribution();

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
        <BarChart3 className="w-4 h-4 mr-2" />
        Assignment Analytics
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <div className="text-2xl font-bold text-blue-800">
            {analytics.totalSubmissions}
          </div>
          <div className="text-sm text-blue-600">Total Submissions</div>
        </div>

        <div className="text-center p-4 bg-green-50 rounded-lg">
          <div className="text-2xl font-bold text-green-800">
            {analytics.gradedCount}
          </div>
          <div className="text-sm text-green-600">Graded</div>
        </div>

        <div className="text-center p-4 bg-yellow-50 rounded-lg">
          <div className="text-2xl font-bold text-yellow-800">
            {analytics.lateSubmissions}
          </div>
          <div className="text-sm text-yellow-600">Late Submissions</div>
        </div>

        <div className="text-center p-4 bg-purple-50 rounded-lg">
          <div className="text-2xl font-bold text-purple-800">
            {analytics.averageScore.toFixed(1)}%
          </div>
          <div className="text-sm text-purple-600">Average Score</div>
        </div>
      </div>

      <div>
        <h4 className="font-medium text-gray-900 mb-3">Grade Distribution</h4>
        <div className="space-y-2">
          {distribution.map((range, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className="w-16 text-sm text-gray-600">{range.name}</div>
              <div className="flex-1 bg-gray-200 rounded-full h-4 relative">
                <div
                  className={`${range.color} h-4 rounded-full transition-all duration-300`}
                  style={{ width: `${range.percentage}%` }}
                />
              </div>
              <div className="w-16 text-sm text-gray-900 text-right">
                {range.count} ({range.percentage.toFixed(1)}%)
              </div>
            </div>
          ))}
        </div>
      </div>

      {analytics.commonFeedback && analytics.commonFeedback.length > 0 && (
        <div className="mt-6">
          <h4 className="font-medium text-gray-900 mb-3">
            Common Feedback Themes
          </h4>
          <div className="space-y-2">
            {analytics.commonFeedback.map((feedback, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-2 bg-gray-50 rounded"
              >
                <span className="text-sm text-gray-800">{feedback.theme}</span>
                <span className="text-xs text-gray-500">
                  {feedback.frequency} mentions
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const TeacherGradingDashboard: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();

  const [state, setState] = useState<GradingState>({
    assignment: null,
    submissions: [],
    selectedSubmission: null,
    rubric: null,
    analytics: null,
    loading: true,
    error: null,
    filterStatus: "all",
    searchQuery: "",
    gradingMode: "individual",
    selectedSubmissions: [],
    currentGrade: {},
    feedback: "",
    showRubric: true,
    showAnalytics: false,
  });

  useEffect(() => {
    if (assignmentId) {
      loadAssignmentData();
    }
  }, [assignmentId]);

  const loadAssignmentData = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const [assignmentRes, submissionsRes, rubricRes, analyticsRes] =
        await Promise.all([
          apiClient.get(`/elearning/assignments/${assignmentId}`),
          apiClient.get(`/elearning/assignments/${assignmentId}/submissions`),
          apiClient.get(`/elearning/assignments/${assignmentId}/rubric`),
          apiClient.get(`/elearning/assignments/${assignmentId}/analytics`),
        ]);

      setState((prev) => ({
        ...prev,
        assignment: assignmentRes.data,
        submissions: submissionsRes.data,
        rubric: rubricRes.data,
        analytics: analyticsRes.data,
        loading: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error:
          error instanceof Error
            ? error.message
            : "Failed to load assignment data",
        loading: false,
      }));
    }
  };

  const selectSubmission = (submission: AssignmentSubmission) => {
    setState((prev) => ({
      ...prev,
      selectedSubmission: submission,
      currentGrade: submission.grade || {},
      feedback: submission.grade?.feedback || "",
    }));
  };

  const updateGrade = (criterion: string, score: number, feedback?: string) => {
    setState((prev) => ({
      ...prev,
      currentGrade: {
        ...prev.currentGrade,
        rubricScores: {
          ...prev.currentGrade.rubricScores,
          [criterion]: score,
        },
        criteriaFeedback: feedback
          ? {
              ...prev.currentGrade.criteriaFeedback,
              [criterion]: feedback,
            }
          : prev.currentGrade.criteriaFeedback,
      },
    }));
  };

  const calculateTotalScore = () => {
    if (!state.rubric || !state.currentGrade.rubricScores) return 0;

    const totalPoints = Object.values(state.currentGrade.rubricScores).reduce(
      (sum, score) => sum + score,
      0
    );
    const maxPoints = state.rubric.criteria.reduce(
      (sum, criterion) => sum + criterion.maxScore,
      0
    );

    return maxPoints > 0 ? Math.round((totalPoints / maxPoints) * 100) : 0;
  };

  const saveGrade = async () => {
    if (!state.selectedSubmission) return;

    try {
      const gradeData = {
        score: calculateTotalScore(),
        rubricScores: state.currentGrade.rubricScores,
        criteriaFeedback: state.currentGrade.criteriaFeedback,
        feedback: state.feedback,
        gradedBy: "teacher123", // Replace with actual teacher ID
        gradedAt: new Date().toISOString(),
      };

      await apiClient.post(
        `/elearning/assignments/${assignmentId}/submissions/${state.selectedSubmission._id}/grade`,
        gradeData
      );

      // Update local state
      setState((prev) => ({
        ...prev,
        submissions: prev.submissions.map((sub) =>
          sub._id === state.selectedSubmission?._id
            ? { ...sub, grade: gradeData, status: "graded" }
            : sub
        ),
        selectedSubmission: {
          ...state.selectedSubmission,
          grade: gradeData,
          status: "graded",
        },
      }));

      // Reload analytics
      const analyticsRes = await apiClient.get(
        `/elearning/assignments/${assignmentId}/analytics`
      );
      setState((prev) => ({ ...prev, analytics: analyticsRes.data }));
    } catch (error) {
      console.error("Error saving grade:", error);
      // Show error toast
    }
  };

  const downloadSubmission = (submission: AssignmentSubmission) => {
    // In a real implementation, this would download the submission files
    console.log("Downloading submission:", submission._id);
  };

  const exportGrades = async () => {
    try {
      const response = await apiClient.get(
        `/elearning/assignments/${assignmentId}/grades/export`,
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${state.assignment?.title}_grades.csv`);
      
      try {
        document.body.appendChild(link);
        link.click();
      } finally {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error exporting grades:", error);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading assignment data...</p>
        </div>
      </div>
    );
  }

  if (state.error || !state.assignment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Assignment
          </h2>
          <p className="text-gray-600 mb-4">{state.error}</p>
          <button
            onClick={() => navigate("/elearning/assignments")}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Assignments
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <button
                  onClick={() => navigate("/elearning/assignments")}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <h1 className="text-2xl font-bold text-gray-900">
                  {state.assignment.title}
                </h1>
              </div>

              <p className="text-gray-600 mb-4">
                {state.assignment.description}
              </p>

              <div className="flex items-center space-x-6 text-sm text-gray-500">
                <div className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>
                    Due:{" "}
                    {new Date(state.assignment.dueDate).toLocaleDateString()}
                  </span>
                </div>

                <div className="flex items-center space-x-1">
                  <Star className="w-4 h-4" />
                  <span>Max Score: {state.assignment.maxScore} points</span>
                </div>

                <div className="flex items-center space-x-1">
                  <Users className="w-4 h-4" />
                  <span>{state.submissions.length} submissions</span>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    showAnalytics: !prev.showAnalytics,
                  }))
                }
                className={`flex items-center space-x-2 px-3 py-2 text-sm border rounded-lg transition-colors ${
                  state.showAnalytics
                    ? "border-blue-300 bg-blue-50 text-blue-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                <span>Analytics</span>
              </button>

              <button
                onClick={exportGrades}
                className="flex items-center space-x-2 px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <Download className="w-4 h-4" />
                <span>Export Grades</span>
              </button>
            </div>
          </div>
        </div>

        {/* Analytics Overview */}
        {state.showAnalytics && state.analytics && (
          <div className="mb-6">
            <AnalyticsOverview analytics={state.analytics} />
          </div>
        )}

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search students..."
                  value={state.searchQuery}
                  onChange={(e) =>
                    setState((prev) => ({
                      ...prev,
                      searchQuery: e.target.value,
                    }))
                  }
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <select
                value={state.filterStatus}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    filterStatus: e.target.value as any,
                  }))
                }
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Submissions</option>
                <option value="submitted">Submitted</option>
                <option value="graded">Graded</option>
                <option value="late">Late</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            <div className="flex items-center space-x-3">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={state.showRubric}
                  onChange={(e) =>
                    setState((prev) => ({
                      ...prev,
                      showRubric: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Show Rubric</span>
              </label>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Submission List */}
          <div className="lg:col-span-1">
            <SubmissionList
              submissions={state.submissions}
              selectedSubmission={state.selectedSubmission}
              onSelectSubmission={selectSubmission}
              searchQuery={state.searchQuery}
              filterStatus={state.filterStatus}
            />
          </div>

          {/* Grading Panel */}
          <div className="lg:col-span-2 space-y-6">
            {state.selectedSubmission ? (
              <>
                {/* Submission Details */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {state.selectedSubmission.studentName}
                      </h3>
                      <p className="text-sm text-gray-600">
                        ID: {state.selectedSubmission.studentId}
                      </p>
                      <p className="text-sm text-gray-600">
                        Submitted:{" "}
                        {new Date(
                          state.selectedSubmission.submittedAt
                        ).toLocaleString()}
                      </p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() =>
                          downloadSubmission(state.selectedSubmission!)
                        }
                        className="p-2 text-gray-400 hover:text-blue-600 rounded"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Submission Content */}
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-2">
                      Submission
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {state.selectedSubmission.content ? (
                        <div
                          className="prose max-w-none"
                          dangerouslySetInnerHTML={{
                            __html: state.selectedSubmission.content,
                          }}
                        />
                      ) : (
                        <p className="text-gray-500 italic">No text content</p>
                      )}
                    </div>
                  </div>

                  {/* Attached Files */}
                  {state.selectedSubmission.attachments &&
                    state.selectedSubmission.attachments.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-medium text-gray-900 mb-2">
                          Attachments
                        </h4>
                        <div className="space-y-2">
                          {state.selectedSubmission.attachments.map(
                            (file, index) => (
                              <div
                                key={index}
                                className="flex items-center space-x-2 p-2 bg-gray-50 rounded"
                              >
                                <Paperclip className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-900">
                                  {file.name}
                                </span>
                                <button className="text-blue-600 hover:text-blue-800 text-sm">
                                  View
                                </button>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                  {/* Current Grade Display */}
                  {state.selectedSubmission.grade && (
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-blue-900">
                          Current Grade
                        </span>
                        <span className="text-xl font-bold text-blue-900">
                          {state.selectedSubmission.grade.score}%
                        </span>
                      </div>
                      {state.selectedSubmission.grade.feedback && (
                        <p className="mt-2 text-sm text-blue-800">
                          {state.selectedSubmission.grade.feedback}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Rubric Grading */}
                {state.showRubric && state.rubric && (
                  <RubricGrading
                    rubric={state.rubric}
                    currentGrade={state.currentGrade}
                    onGradeChange={updateGrade}
                  />
                )}

                {/* Overall Feedback */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    Overall Feedback
                  </h3>

                  <textarea
                    value={state.feedback}
                    onChange={(e) =>
                      setState((prev) => ({
                        ...prev,
                        feedback: e.target.value,
                      }))
                    }
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Provide overall feedback for this submission..."
                  />

                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-gray-600">
                      Total Score:{" "}
                      <span className="font-semibold">
                        {calculateTotalScore()}%
                      </span>
                    </div>

                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => {
                          setState((prev) => ({
                            ...prev,
                            currentGrade: state.selectedSubmission?.grade || {},
                            feedback:
                              state.selectedSubmission?.grade?.feedback || "",
                          }));
                        }}
                        className="flex items-center space-x-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                      >
                        <RotateCcw className="w-4 h-4" />
                        <span>Reset</span>
                      </button>

                      <button
                        onClick={saveGrade}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        <Save className="w-4 h-4" />
                        <span>Save Grade</span>
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Select a Submission
                </h3>
                <p className="text-gray-600">
                  Choose a submission from the list to start grading
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherGradingDashboard;
